// App.tsx
import React, { useState, useEffect } from 'react';
import Icon from './Icon.tsx';
import RecycleBin from './applications/RecycleBin.tsx';
import Window from './applications/Window.tsx';
import LogPassword from './applications/LogPassword.tsx';
import LogContents from './applications/LogContents.tsx';
import Camera from './applications/Camera.tsx';
import Update from './applications/Update.tsx';
import FileViewer from './applications/FileViewer.tsx';
import UpdateFail from './applications/UpdateFail.tsx';
import Loading from './applications/Loading.tsx';
import { soundService } from './services/SoundService.ts';
import { auth, onAuthStateChanged } from './config/firebaseConfig';

// @ts-ignore
import recycleBinIcon from './assets/icons/recycle_bin.png';
// @ts-ignore
import cameraIcon from './assets/icons/camera.png';
// @ts-ignore
import logIcon from './assets/icons/log.png';
// @ts-ignore
import updateIcon from './assets/icons/update.png';
// @ts-ignore
import startButtonIcon from './assets/icons/start-button.png';
// @ts-ignore
import linkIcon from './assets/icons/link.png';
// @ts-ignore
import txtIcon from './assets/icons/document_01.png';
// @ts-ignore
import pdfIcon from './assets/icons/document_02.png';
// @ts-ignore
import "./App.css";

interface WindowData {
    type: string;
    props?: any;
    id: number;
    isActive?: boolean;
  }
  
  const App: React.FC = () => {
    const [windows, setWindows] = useState<WindowData[]>([]);
    const [devToolsDetected, setDevToolsDetected] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [isStartMenuOpen, setIsStartMenuOpen] = useState(false);
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [currentPath] = useState(window.location.pathname);
    const [redirectedPath] = useState<string | null>(null);
    const is404 = currentPath !== '/' || redirectedPath !== null;
  
    useEffect(() => {
      soundService.init();

      const unsubscribe = onAuthStateChanged(auth, (user) => {
        if (user) {
          setIsAuthenticated(true);
        } else {
          setIsAuthenticated(false);
        }
      });
  
      const originalArt = `
.                  ..MMMMMMMMMMMNJ
                  .MMMMMMMMMMMMMMMM,
                .MMMM#^        ?MMMMN,
              .JMMMMY            TMMMMm.   .gggga.
             .MMMM@               .WMMMN,   .WMMMM,
           .MMMMM^                  (MMMMN,   (MMMMN,
   .MMMMMMMMMMMMMMMMMMMMMMMMMMMNN,    TMMMMe    TMMMMe    UMMMM,
 .MMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMa   .WMMMN,   .WMMMN,   (MMMMN,
                               ?HMMMN,   ?MMMMN.   /MMMMN.   TMMMMe.
                                 ?MMMMh.   TMMMMe    TMMMMe   .WMMMN,
                                   TMMMMa   .HMMMN,   .WMMMN,   /MMMMN,
                                    ,MMMMN,   ?MMMMh.   ?MMMMh.   TMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMMM%
                                      ?MMMMm.   TMMMMa    TMMMMa    TMMMMMMMMMMMMMMMMMMMMMMMMMMMMMD
                                                 ,MMMMN,   ,HMMMN,                  .dMMMM=
                                                   ?MMMMm.   ?MMMMm.               .MMMMD
                                                     TMMMM;    TMMMMa            .MMMM#'
                                                                ,MMMMN,        .JMMMM3
                                                                  7MMMMMMMMMMMMMMMMD
                                                                    TMMMMMMMMMMMM9\`
`.trim();

      const checkDevTools = () => {
        if (devToolsDetected) return;

        const devtools = /./;
        devtools.toString = function() {
          if (!devToolsDetected) {
            console.log('%c彼女を認識し続けてください\n彼女が在り続けるために必要な行為です', 'color: currentColor; font-size: 20px; font-weight: bold;');
            console.log(originalArt);
            setDevToolsDetected(true);
          }
          return '';
        };
        console.log('%c', devtools);
      };
  
      const interval = setInterval(checkDevTools, 1000);
  
      return () => {
        clearInterval(interval);
        unsubscribe();
      };
    }, [devToolsDetected]);
  
    useEffect(() => {
    }, [windows]);
  
    const activateWindow = (id: number) => {
      setWindows(windows.map(win => ({
        ...win,
        isActive: win.id === id
      })));
    };
  
    const openWindow = (type: string, props = {}) => {
      const existingWindow = windows.find(win => win.type === type);
      
      if (existingWindow) {
        activateWindow(existingWindow.id);
        return;
      }
      
      const newWindow = { type, props, id: Date.now(), isActive: true };
      setWindows([...windows.map(w => ({...w, isActive: false})), newWindow]);
    };
  
    const closeWindow = (id: number) => {
      setWindows(prevWindows => {
        const filteredWindows = prevWindows.filter(win => win.id !== id);
        return filteredWindows;
      });
    };

    const handleAuthenticated = (passwordWindowId: number) => {
      const updatedWindows = windows.filter(win => win.id !== passwordWindowId);
      setWindows(updatedWindows);
      setTimeout(() => {
        const newWindow = { type: 'log-contents', props: {}, id: Date.now(), isActive: true };
        setWindows([...updatedWindows.map(w => ({...w, isActive: false})), newWindow]);
      }, 100);
    };

    const handleUpdateFail = (updateWindowId: number) => {
      const updatedWindows = windows.filter(win => win.id !== updateWindowId);
      setWindows(updatedWindows);
      setTimeout(() => {
        const newWindow = { type: 'updatefail', props: {}, id: Date.now(), isActive: true };
        setWindows([...updatedWindows.map(w => ({...w, isActive: false})), newWindow]);
      }, 100);
    };

    const handleOpenFile = (type: string, filename: string, windowType: string) => {
      const existingWindow = windows.find(win => 
        win.type === windowType && 
        win.props?.type === type && 
        win.props?.filename === filename
      );
      
      if (existingWindow) {
        activateWindow(existingWindow.id);
        return;
      }

      const newWindow = { 
        type: windowType, 
        props: { type, filename }, 
        id: Date.now(), 
        isActive: true 
      };
      setWindows([...windows.map(w => ({...w, isActive: false})), newWindow]);
    };

    const getWindowTitle = (type: string) => {
      switch(type) {
        case 'recycle_bin':
          return 'Recycle Bin';
        case 'camera':
          return 'Camera';
        case 'log':
          return 'Log';
        case 'log-password':
          return 'Enter Password';
        case 'log-contents':
          return 'Log';
        case 'update':
          return 'Update';
        case 'updatefail':
          return 'Update';
        case 'file-viewer-txt':
        case 'file-viewer-pdf':
          return 'File Viewer';
        default:
          return 'Window';
      }
    };

    const handleLoadingComplete = () => {
      setIsLoading(false);
    };


    return (
      <div className="App">
        {isLoading || !isAuthenticated ? (
          <Loading 
            onLoadingComplete={isAuthenticated ? handleLoadingComplete : undefined} 
            is404={is404}
          />
        ) : (
          <>
            <div className="noise-overlay"></div>
            <div 
              className="desktop"
              onClick={() => soundService.playClickSound()}
            >
              <div className="icons">
                <div 
                  className="iconContainer"
                  style={{ 
                    gridColumn: 1, 
                    gridRow: 1 
                  }}
                >
                  <Icon
                    image={recycleBinIcon}
                    name="Recycle bin"
                    onDoubleClick={() => openWindow('recycle_bin')}
                  />
                </div>
                <div 
                  className="iconContainer"
                  style={{ 
                    gridColumn: 1, 
                    gridRow: 2 
                  }}
                >
                  <Icon
                    image={cameraIcon}
                    name="Camera"
                    onDoubleClick={() => openWindow('camera')}
                  />
                </div>
                <div 
                  className="iconContainer"
                  style={{ 
                    gridColumn: 1, 
                    gridRow: 3 
                  }}
                >
                  <Icon
                    image={logIcon}
                    name="Log"
                    onDoubleClick={() => openWindow('log-password')}
                  />
                </div>
                <div 
                  className="iconContainer"
                  style={{ 
                    gridColumn: 1, 
                    gridRow: 4 
                  }}
                >
                  <Icon
                    image={updateIcon}
                    name="Update"
                    onDoubleClick={() => openWindow('update')}
                  />
                </div>
                <div 
                  className="iconContainer"
                  style={{ 
                    gridColumn: 1, 
                    gridRow: 5 
                  }}
                >
                  <Icon
                    image={linkIcon}
                    name="2025/05/06"
                    onDoubleClick={() => window.open('https://youtube.com/live/eQjR_eHqz9Y?feature=share', '_blank')}
                  />
                </div>
              </div>
      
              {windows.map((win) => (
                <Window 
                  key={win.id} 
                  onClose={() => closeWindow(win.id)}
                  windowType={win.type}
                  onClick={() => activateWindow(win.id)}
                  isActive={win.isActive}
                  title={getWindowTitle(win.type)}
                  icon={win.type === 'recycle_bin' ? recycleBinIcon :
                        win.type === 'camera' ? cameraIcon :
                        win.type === 'log' || win.type === 'log-password' || win.type === 'log-contents' ? logIcon :
                        win.type === 'update' || win.type === 'updatefail' ? updateIcon :
                        win.type === 'file-viewer-txt' || win.type === 'file-viewer-pdf' ? (win.props.type === 'txt' ? txtIcon : pdfIcon) :
                        undefined}
                >
                  {win.type === 'recycle_bin' && <RecycleBin onClose={() => closeWindow(win.id)} />}
                  {win.type === 'camera' && <Camera />}
                  {win.type === 'log-password' && <LogPassword onAuthenticated={() => handleAuthenticated(win.id)} />}
                  {win.type === 'log-contents' && <LogContents onOpenFile={handleOpenFile} />}
                  {win.type === 'update' && (
                    <Update 
                      onClose={(props) => {
                        if (props?.openUpdateFail === true) {
                          handleUpdateFail(win.id);
                        } else {
                          const updatedWindows = windows.filter(w => w.id !== win.id);
                          setWindows(updatedWindows);
                          setTimeout(() => {
                            setWindows(prev => prev.filter(w => w.id !== win.id));
                          }, 100);
                        }
                      }} 
                    />
                  )}
                  {win.type === 'updatefail' && <UpdateFail onClose={() => closeWindow(win.id)} />}
                  {(win.type === 'file-viewer-txt' || win.type === 'file-viewer-pdf') && <FileViewer type={win.props.type} filename={win.props.filename} />}
                </Window>
              ))}
              <div className="taskbar">
                <div className="start-menu">
                  <button 
                    className="start-button"
                    onClick={() => setIsStartMenuOpen(!isStartMenuOpen)}
                  >
                    <img src={startButtonIcon} className="start-button-icon" alt='start-button'/>
                  </button>
                  {isStartMenuOpen && (
                    <div className="start-menu-container">
                      <div className="start-menu-header">
                        admin.somunia.net
                      </div>
                      <div 
                        className="start-menu-item"
                        onClick={() => openWindow('recycle_bin')}
                      >
                        <img src={recycleBinIcon} alt="recycle bin" />
                        <span>Recycle Bin</span>
                      </div>
                      <div 
                        className="start-menu-item"
                        onClick={() => openWindow('camera')}
                      >
                        <img src={cameraIcon} alt="camera" />
                        <span>Camera</span>
                      </div>
                      <div 
                        className="start-menu-item"
                        onClick={() => openWindow('log-password')}
                      >
                        <img src={logIcon} alt="log" />
                        <span>Log</span>
                      </div>
                      <div 
                        className="start-menu-item"
                        onClick={() => openWindow('update')}
                      >
                        <img src={updateIcon} alt="update" />
                        <span>Update</span>
                      </div>
                      <div 
                        className="start-menu-item"
                        onClick={() => window.open('https://youtube.com/live/eQjR_eHqz9Y?feature=share', '_blank')}
                      >
                        <img src={linkIcon} alt="link" />
                        <span>2025/05/06</span>
                      </div>
                    </div>
                  )}
                </div>
                
                <div className="taskbar-apps">
                  {windows.map((win) => (
                    <div 
                      className={`taskbar-app ${win.isActive ? 'active' : ''}`} 
                      key={win.id}
                      onClick={() => activateWindow(win.id)}
                    >
                      <img 
                        src={win.type === 'recycle_bin' ? recycleBinIcon :
                             win.type === 'camera' ? cameraIcon :
                             win.type === 'log' || win.type === 'log-password' || win.type === 'log-contents' ? logIcon :
                             win.type === 'update' || win.type === 'updatefail' ? updateIcon :
                             win.type === 'file-viewer-txt' || win.type === 'file-viewer-pdf' ? (win.props.type === 'txt' ? txtIcon : pdfIcon) :
                             undefined}
                        alt=""
                        className="taskbar-app-icon"
                      />
                      <span className="taskbar-app-title">{getWindowTitle(win.type)}</span>
                    </div>
                  ))}
                  <div className='taskbar-clock'>
                    <span className='taskbar-clock-text'>{new Date().toLocaleTimeString('ja-JP', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    );
  };
  
  export default App;